import {
  type AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  type ElementRef,
  inject,
  type OnDestroy,
  type OnInit,
  type Signal,
  signal,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive, RouterModule } from '@angular/router';
import { ChevronLeft, ChevronRight, LucideAngularModule } from 'lucide-angular';
import { filter, map } from 'rxjs/operators';
import type { LearningAtLunchItemModel } from '../../models/learning-at-lunch-item.model';
import { EventService } from '../../services/event.service';
import { type NavItem, NavigationService } from '../../services/navigation.service';

@Component({
  selector: 'app-header-navigation',
  standalone: true,
  imports: [LucideAngularModule, RouterLink, RouterLinkActive, RouterModule],
  templateUrl: './header-navigation.component.html',
})
export class HeaderNavigationComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mobileScrollContainer', { static: false }) mobileScrollContainer!: ElementRef<HTMLDivElement>;

  // Navigation properties
  homeNavItems: NavItem[] = [];
  eventNavItems: Signal<NavItem[]>;
  eventType: Signal<string>;
  eventDocumentId: Signal<string>;
  isMobileMenuOpen = false;
  showLeftChevron = false;
  showRightChevron = false;
  readonly chevronLeft = ChevronLeft;
  readonly chevronRight = ChevronRight;
  private resizeObserver?: ResizeObserver;

  // Page header properties
  currentUrl = signal('');

  // Services
  private eventService = inject(EventService);
  private router = inject(Router);
  private navigationService = inject(NavigationService);
  private activatedRoute = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);

  constructor() {
    // Initialize navigation properties
    this.homeNavItems = this.navigationService.homeNavItems;
    this.eventNavItems = computed(() => this.filterByEventType(this.navigationService.eventNavItems));
    this.eventType = computed(() => this.eventService.getCurrentEvent()?.type.toString() ?? '');
    this.eventDocumentId = computed(() => this.eventService.getCurrentEvent()?.documentId ?? '');
  }

  ngOnInit() {
    // Initialize currentUrl with current router url
    this.currentUrl.set(this.router.url);

    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.getChildRoute(this.activatedRoute)),
      filter((route) => route !== null)
    );

    // Update currentUrl signal on route changes and center active item
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.currentUrl.set(event.url);
      // Auto-center the active item after navigation with a delay to ensure DOM is updated
      setTimeout(() => this.centerActiveItem(), 100);
    });
  }

  // Page header computed properties
  pageTitle = computed(() => {
    const currentEvent = this.eventService.getCurrentEvent();
    const url = this.currentUrl();

    // Check if we're on an event page
    if (url.includes('/event/')) {
      if (currentEvent) {
        if (currentEvent.type === 'LearningAtLunch') {
          return (currentEvent as LearningAtLunchItemModel).topic || 'Learning at Lunch';
        }
        return currentEvent.title || 'Event';
      }
      return 'Event';
    }

    // Check for other specific routes
    if (url === '/' || url === '/home') {
      return 'Home';
    }

    // Check for agenda pages
    if (url.includes('/agenda')) {
      return 'Agenda';
    }

    // Check for location pages
    if (url.includes('/location')) {
      return 'Location';
    }

    // Check for feedback pages
    if (url.includes('/feedback')) {
      return 'Feedback';
    }

    // Check for check-in pages
    if (url.includes('/check-in')) {
      return 'Check-in';
    }

    // Default fallback
    return 'Event Hub';
  });

  showStartseiteLink = computed(() => {
    const url = this.currentUrl();
    return url !== '/' && url !== '/home';
  });

  showPageHeader = computed(() => {
    const url = this.currentUrl();
    // Hide header on login, auth callback, and home routes
    return !url.includes('/login') && !url.includes('/auth/microsoft/redirect') && url !== '/' && url !== '/home';
  });

  showNavigation = computed(() => {
    const url = this.currentUrl();
    // Hide entire navigation component on login, auth callback, and home routes
    return !url.includes('/login') && !url.includes('/auth/microsoft/redirect') && url !== '/' && url !== '/home';
  });

  hasNavigationItems = computed(() => {
    return this.homeNavItems.length > 0 || this.eventNavItems().length > 1;
  });

  // Navigation methods
  isActive(path: string): boolean {
    return this.router.url.includes(path);
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  ngAfterViewInit(): void {
    // Multiple checks to ensure chevrons appear after rendering
    setTimeout(() => this.updateChevronVisibility(), 100);
    setTimeout(() => this.updateChevronVisibility(), 300);
    setTimeout(() => this.updateChevronVisibility(), 500);

    // Center the active item on initial load
    setTimeout(() => this.centerActiveItem(), 200);
    setTimeout(() => this.centerActiveItem(), 600);

    // Add resize observer to handle window resize events
    if (typeof ResizeObserver !== 'undefined' && this.mobileScrollContainer) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateChevronVisibility();
        // Re-center active item when container size changes
        setTimeout(() => this.centerActiveItem(), 100);
      });
      this.resizeObserver.observe(this.mobileScrollContainer.nativeElement);
    }

    // Add mutation observer to detect content changes
    if (typeof MutationObserver !== 'undefined' && this.mobileScrollContainer) {
      const mutationObserver = new MutationObserver(() => {
        this.updateChevronVisibility();
        // Re-center active item when content changes
        setTimeout(() => this.centerActiveItem(), 100);
      });
      mutationObserver.observe(this.mobileScrollContainer.nativeElement, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  onScroll(): void {
    this.updateChevronVisibility();
  }

  scrollLeft(): void {
    const container = this.mobileScrollContainer.nativeElement;
    container.scrollBy({ left: -150, behavior: 'smooth' });
    // Update chevron visibility after smooth scroll animation
    setTimeout(() => this.updateChevronVisibility(), 300);
  }

  scrollRight(): void {
    const container = this.mobileScrollContainer.nativeElement;
    container.scrollBy({ left: 150, behavior: 'smooth' });
    // Update chevron visibility after smooth scroll animation
    setTimeout(() => this.updateChevronVisibility(), 300);
  }

  centerActiveItem(): void {
    const container = this.mobileScrollContainer?.nativeElement;
    if (!container) return;

    // Find the active navigation item
    const activeItem = container.querySelector('a.active, a[class*="from-purple-500"]') as HTMLElement;
    if (!activeItem) return;

    // Calculate the position to center the active item
    const containerWidth = container.clientWidth;
    const itemLeft = activeItem.offsetLeft;
    const itemWidth = activeItem.offsetWidth;
    const itemCenter = itemLeft + itemWidth / 2;
    const containerCenter = containerWidth / 2;

    // Calculate scroll position to center the item
    const scrollPosition = itemCenter - containerCenter;

    // Ensure we don't scroll beyond the boundaries
    const maxScroll = container.scrollWidth - containerWidth;
    const finalScrollPosition = Math.max(0, Math.min(scrollPosition, maxScroll));

    // Scroll to position with smooth behavior
    container.scrollTo({
      left: finalScrollPosition,
      behavior: 'smooth',
    });

    // Update chevron visibility after centering
    setTimeout(() => this.updateChevronVisibility(), 300);
  }

  private updateChevronVisibility(): void {
    const container = this.mobileScrollContainer?.nativeElement;
    if (!container) return;

    // Don't show chevrons if there are no navigation items
    if (!this.hasNavigationItems()) {
      this.showLeftChevron = false;
      this.showRightChevron = false;
      this.cdr.detectChanges();
      return;
    }

    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;
    const scrollableWidth = scrollWidth - clientWidth;

    // Only show chevrons if content is scrollable
    if (scrollableWidth > 0) {
      // Add threshold for sub-pixel rendering differences
      const threshold = 1;

      // Show left chevron if not at the beginning
      this.showLeftChevron = scrollLeft > threshold;

      // Show right chevron if not at the end
      this.showRightChevron = scrollLeft < scrollableWidth - threshold;

      // Force change detection
      this.cdr.detectChanges();
    } else {
      this.showLeftChevron = false;
      this.showRightChevron = false;

      // Force change detection
      this.cdr.detectChanges();
    }
  }

  private filterByEventType(navItems: NavItem[]): NavItem[] {
    if (!this.eventService.getCurrentEvent()) {
      return [];
    }
    const eventType = this.eventService.getCurrentEvent()?.type;
    return navItems.filter((item: NavItem) => {
      switch (item.path) {
        case 'locations':
          // 'NewsAtFourEnergy' and 'LearningAtLunch' don't have a location
          return eventType === 'Afterwork' || eventType === 'Offsite' || eventType === 'Weihnachtsfeier';
        case 'agenda':
          // 'NewsAtFourEnergy' and 'LearningAtLunch' don't have an agenda
          return eventType === 'Afterwork' || eventType === 'Offsite' || eventType === 'Weihnachtsfeier';
        case 'updates':
          // All event types have updates
          return true;
        case 'questions':
          // 'LearningAtLunch' and 'NewsAtFourEnergy' don't have questions
          return eventType === 'Afterwork' || eventType === 'Offsite' || eventType === 'Weihnachtsfeier';
        default:
          return true;
      }
    });
  }

  private getChildRoute(route: ActivatedRoute): ActivatedRoute | null {
    while (route.firstChild) {
      route = route.firstChild;
      if (route.snapshot.data.gradient) {
        return route;
      }
    }
    return route;
  }
}
